import React, { useState, useEffect } from 'react';
import { Dialog } from '@headlessui/react';
import { XMarkIcon, MagnifyingGlassIcon, CheckIcon } from '@heroicons/react/24/outline';
import { Issue, IssueStatus } from '../../types';
import { getProjectIssues } from '../../services/api/issue';
import { useToast } from '../ToastContainer';

interface IssueSelectorProps {
  projectId: string;
  selectedIssueIds: string[];
  isOpen: boolean;
  onClose: () => void;
  onSelect: (issueIds: string[]) => void;
}

const IssueSelector: React.FC<IssueSelectorProps> = ({
  projectId,
  selectedIssueIds,
  isOpen,
  onClose,
  onSelect
}) => {
  const [issues, setIssues] = useState<Issue[]>([]);
  const [filteredIssues, setFilteredIssues] = useState<Issue[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set(selectedIssueIds));
  const [isLoading, setIsLoading] = useState(false);
  const [statusFilter, setStatusFilter] = useState<string>('ALL');
  const { showError } = useToast();

  // 載入議題列表
  useEffect(() => {
    if (isOpen && projectId) {
      loadIssues();
    }
  }, [isOpen, projectId]);

  // 更新選中狀態
  useEffect(() => {
    setSelectedIds(new Set(selectedIssueIds));
  }, [selectedIssueIds]);

  // 篩選議題
  useEffect(() => {
    let filtered = issues;

    // 搜尋篩選
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(issue => 
        issue.title.toLowerCase().includes(term) ||
        issue.key.toLowerCase().includes(term) ||
        issue.description?.toLowerCase().includes(term)
      );
    }

    // 狀態篩選
    if (statusFilter !== 'ALL') {
      filtered = filtered.filter(issue => {
        if (issue.boardStatus) {
          return issue.boardStatus.id === statusFilter;
        }
        return issue.status === statusFilter;
      });
    }

    // 排除已封存和已刪除的議題
    filtered = filtered.filter(issue => !issue.archived && !issue.deleted);

    setFilteredIssues(filtered);
  }, [issues, searchTerm, statusFilter]);

  const loadIssues = async () => {
    try {
      setIsLoading(true);
      console.log('Loading issues for projectId:', projectId);
      const response = await getProjectIssues(projectId);
      console.log('API response:', response);
      console.log('Response data:', response.data);
      const issues = response.data?.issues || [];
      console.log('Issues array:', issues);
      setIssues(issues);
    } catch (error) {
      console.error('Failed to load issues:', error);
      showError('載入議題失敗');
    } finally {
      setIsLoading(false);
    }
  };

  const toggleIssue = (issueId: string) => {
    const newSelectedIds = new Set(selectedIds);
    if (newSelectedIds.has(issueId)) {
      newSelectedIds.delete(issueId);
    } else {
      newSelectedIds.add(issueId);
    }
    setSelectedIds(newSelectedIds);
  };

  const handleConfirm = () => {
    onSelect(Array.from(selectedIds));
    onClose();
  };

  const getStatusColor = (issue: Issue) => {
    if (issue.boardStatus) {
      return issue.boardStatus.color;
    }
    // 預設狀態顏色
    switch (issue.status) {
      case IssueStatus.TODO:
        return '#6b7280';
      case IssueStatus.IN_PROGRESS:
        return '#3b82f6';
      case IssueStatus.IN_REVIEW:
        return '#f59e0b';
      case IssueStatus.DONE:
        return '#10b981';
      default:
        return '#6b7280';
    }
  };

  const getStatusText = (issue: Issue) => {
    if (issue.boardStatus) {
      return issue.boardStatus.name;
    }
    // 預設狀態文字
    switch (issue.status) {
      case IssueStatus.TODO:
        return '待辦';
      case IssueStatus.IN_PROGRESS:
        return '進行中';
      case IssueStatus.IN_REVIEW:
        return '待審核';
      case IssueStatus.DONE:
        return '完成';
      default:
        return issue.status;
    }
  };

  // 獲取唯一的狀態列表
  const uniqueStatuses = Array.from(new Set(issues.map(issue => {
    if (issue.boardStatus) {
      return { id: issue.boardStatus.id, name: issue.boardStatus.name, color: issue.boardStatus.color };
    }
    return { id: issue.status, name: getStatusText(issue), color: getStatusColor(issue) };
  }))).filter((status, index, self) => 
    index === self.findIndex(s => s.id === status.id)
  );

  return (
    <Dialog open={isOpen} onClose={onClose} className="relative z-[60]">
      <div className="fixed inset-0 bg-black/30 z-[60]" aria-hidden="true" />
      
      <div className="fixed inset-0 flex items-center justify-center p-4 z-[61]">
        <Dialog.Panel className="w-full max-w-4xl bg-white rounded-lg shadow-xl max-h-[90vh] flex flex-col">
          <div className="flex items-center justify-between p-6 border-b">
            <div>
              <Dialog.Title className="text-xl font-semibold">
                選擇議題
              </Dialog.Title>
              <p className="text-sm text-gray-600 mt-1">
                已選擇 {selectedIds.size} 個議題
              </p>
            </div>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-500"
            >
              <XMarkIcon className="h-6 w-6" />
            </button>
          </div>

          {/* 搜尋和篩選 */}
          <div className="p-4 border-b bg-gray-50">
            <div className="flex gap-4">
              <div className="flex-1 relative">
                <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                <input
                  type="text"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  placeholder="搜尋議題標題、代碼或描述..."
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="ALL">所有狀態</option>
                {uniqueStatuses.map(status => (
                  <option key={status.id} value={status.id}>
                    {status.name}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {/* 議題列表 */}
          <div className="flex-1 overflow-y-auto p-4">
            {isLoading ? (
              <div className="flex justify-center items-center h-32">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
              </div>
            ) : filteredIssues.length === 0 ? (
              <div className="text-center py-8 text-gray-500">
                沒有找到符合條件的議題
              </div>
            ) : (
              <div className="space-y-2">
                {filteredIssues.map((issue) => (
                  <div
                    key={issue.id}
                    onClick={() => toggleIssue(issue.id)}
                    className={`p-4 rounded-lg border cursor-pointer transition-colors ${
                      selectedIds.has(issue.id)
                        ? 'border-blue-500 bg-blue-50'
                        : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
                    }`}
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          <span className="text-sm font-medium text-gray-500">{issue.key}</span>
                          <span 
                            className="px-2 py-0.5 text-xs font-medium rounded-full text-white"
                            style={{ backgroundColor: getStatusColor(issue) }}
                          >
                            {getStatusText(issue)}
                          </span>
                        </div>
                        <h4 className="font-medium text-gray-900">{issue.title}</h4>
                        {issue.description && (
                          <p className="text-sm text-gray-600 mt-1 line-clamp-2">
                            {issue.description}
                          </p>
                        )}
                        {issue.assignee && (
                          <div className="mt-2 text-sm text-gray-500">
                            負責人：{issue.assignee.name}
                          </div>
                        )}
                      </div>
                      <div className={`ml-4 flex-shrink-0 ${
                        selectedIds.has(issue.id) ? 'text-blue-600' : 'text-gray-300'
                      }`}>
                        <div className="w-6 h-6 rounded border-2 border-current flex items-center justify-center">
                          {selectedIds.has(issue.id) && (
                            <CheckIcon className="h-4 w-4" />
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* 操作按鈕 */}
          <div className="flex justify-between items-center gap-3 p-6 border-t bg-gray-50">
            <button
              onClick={() => setSelectedIds(new Set())}
              className="text-gray-600 hover:text-gray-800"
              disabled={selectedIds.size === 0}
            >
              清除選擇
            </button>
            <div className="flex gap-3">
              <button
                onClick={onClose}
                className="px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
              >
                取消
              </button>
              <button
                onClick={handleConfirm}
                className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
              >
                確認選擇 ({selectedIds.size})
              </button>
            </div>
          </div>
        </Dialog.Panel>
      </div>
    </Dialog>
  );
};

export default IssueSelector;
